import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { X, Pause, Play } from "lucide-react";
import { PLANTILLAS } from "../templates/registry";

const DURACION_MS = 8000;

// Modo Presentación: recorre las portadas de todas las plantillas en bucle,
// rotando además las paletas en cada vuelta. Solo para vidriera/showroom.
export default function Presentacion({ alSalir }) {
  const [paso, setPaso] = useState(0);
  const [pausada, setPausada] = useState(false);
  const reduce = useReducedMotion();

  const indice = paso % PLANTILLAS.length;
  const plantilla = PLANTILLAS[indice];
  const indicePaleta = Math.floor(paso / PLANTILLAS.length) % plantilla.paletas.length;
  const paleta = plantilla.paletas[indicePaleta];

  const datos = useMemo(
    () => ({
      ...plantilla.datosEjemplo,
      tema: paleta.tema,
      secciones: plantilla.datosEjemplo.secciones.map((s) => ({
        ...s,
        visible: s.id === "portada",
      })),
    }),
    [plantilla, paleta]
  );

  useEffect(() => {
    if (pausada) return undefined;
    const id = setInterval(() => setPaso((p) => p + 1), DURACION_MS);
    return () => clearInterval(id);
  }, [pausada]);

  useEffect(() => {
    const alTeclear = (e) => {
      if (e.key === "Escape") alSalir();
      if (e.key === "ArrowRight") setPaso((p) => p + 1);
      if (e.key === "ArrowLeft") setPaso((p) => Math.max(0, p - 1));
      if (e.key === " ") {
        e.preventDefault();
        setPausada((p) => !p);
      }
    };
    window.addEventListener("keydown", alTeclear);
    return () => window.removeEventListener("keydown", alTeclear);
  }, [alSalir]);

  const { Componente } = plantilla;

  return (
    <div className="presentacion" data-testid="presentacion">
      <AnimatePresence mode="wait">
        <motion.div
          className="presentacion-escena"
          key={`${plantilla.id}-${paleta.clave}`}
          initial={{ opacity: 0, scale: reduce ? 1 : 1.02 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: reduce ? 0.05 : 0.9, ease: [0.22, 1, 0.36, 1] }}
        >
          <Componente datos={datos} desactivarScrollSuave />
        </motion.div>
      </AnimatePresence>

      <div className="presentacion-chrome">
        <span className="presentacion-marca">Pro360 · Invitaciones Digitales Plus</span>
        <button
          type="button"
          className="presentacion-salir"
          onClick={alSalir}
          aria-label="Salir de la presentación"
          data-testid="presentacion-salir"
        >
          <X size={16} strokeWidth={2} />
          Salir
        </button>
      </div>

      <div className="presentacion-pie">
        <button
          type="button"
          className="presentacion-pausa"
          onClick={() => setPausada((p) => !p)}
          aria-label={pausada ? "Reanudar presentación" : "Pausar presentación"}
          data-testid="presentacion-pausa"
        >
          {pausada ? <Play size={15} strokeWidth={2} /> : <Pause size={15} strokeWidth={2} />}
        </button>
        <div className="presentacion-info">
          <span className="presentacion-nombre" data-testid="presentacion-nombre">
            {plantilla.nombre}
          </span>
          <span className="presentacion-detalle">
            {plantilla.tipoEtiqueta} · Paleta {paleta.nombre}
          </span>
        </div>
        <div className="presentacion-puntos" aria-hidden="true">
          {PLANTILLAS.map((p, i) => (
            <span
              key={p.id}
              className={`presentacion-punto${i === indice ? " presentacion-punto--activo" : ""}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
